import {
  type TerraUIMessage,
  type TerraUIMessagePart,
  type BaseDataMessage,
  type AskUserMessage,
  type MentionEntity,
  type MessageSegment,
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  ASK_USER_TOOL_NAME,
} from '@terra/shared-dto-chat-types';
import { z } from 'zod';
import { filterSupersededToolCalls } from './superseded-tool-calls';
import { isToolCallData } from './tool-call-data-merge';
import {
  getMentionEntitiesFromUserMessage,
  getMentionSegmentsFromUserMessage,
} from './userMessageDisplayText';
import {
  getMetadataAsUserConfirmation,
  isDataOrConfirmationPart,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';
import type { PendingAskUserInteraction } from './message-processing-types';

export const TextPartSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});

export const TraceMetadataDataSchema = z.looseObject({
  type: z.literal(DataMessageTypeEnum.TraceMetadata),
  traceId: z.string().optional(),
});

export const PartWithIdSchema = z.object({ id: z.string() });
const MIN_VISIBLE_ITEMS_BETWEEN_TODOS = 4;

export function isAskUserData(data: BaseDataMessage): data is AskUserMessage {
  return data.type === ConfirmationMessageTypeEnum.AskUser;
}

export function hasTextContent(part: TerraUIMessagePart): boolean {
  const parsed = TextPartSchema.safeParse(part);

  return parsed.success && parsed.data.text.trim().length > 0;
}

function hasAssistantActivity(msg: TerraUIMessage): boolean {
  if (msg.parts?.some(hasTextContent)) {
    return true;
  }

  return (
    msg.parts?.some((part) => {
      if (!isDataOrConfirmationPart(part) || !part.data) return false;
      const parsed = parseBaseDataMessage(part.data);

      return parsed !== undefined && !isAskUserData(parsed);
    }) ?? false
  );
}

export function extractPendingAskUserInteraction(
  messages: TerraUIMessage[],
): PendingAskUserInteraction | null {
  let sawNewerAssistantActivity = false;
  let selectedValue: string | undefined;
  let answerMentions: MentionEntity[] | undefined;
  let answerSegments: MessageSegment[] | undefined;

  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];

    if (msg.role === 'user') {
      const meta = getMetadataAsUserConfirmation(msg.metadata);

      if (meta?.toolName === ASK_USER_TOOL_NAME) {
        if (sawNewerAssistantActivity) {
          return null;
        }

        selectedValue = typeof meta.response === 'string' ? meta.response : '';
        const mentions = getMentionEntitiesFromUserMessage({ message: msg });
        const segments = getMentionSegmentsFromUserMessage({ message: msg });

        if (mentions.length > 0 && segments && segments.length > 0) {
          answerMentions = mentions;
          answerSegments = segments;
        }

        continue;
      }

      if (msg.parts?.some(hasTextContent)) {
        return null;
      }
    }

    if (msg.role === 'assistant') {
      for (const part of msg.parts ?? []) {
        if (!isDataOrConfirmationPart(part) || !part.data) continue;
        const parsed = parseBaseDataMessage(part.data);

        if (parsed && isAskUserData(parsed)) {
          return {
            dataMessage: parsed,
            selectedValue,
            answerMentions,
            answerSegments,
          };
        }
      }

      if (selectedValue === undefined && hasAssistantActivity(msg)) {
        sawNewerAssistantActivity = true;
      }
    }
  }

  return null;
}

export function getPartId(part: TerraUIMessagePart): string | undefined {
  const partIdResult = PartWithIdSchema.safeParse(part);

  return partIdResult.success ? partIdResult.data.id : undefined;
}

function isTodosDataMessage(
  dataMessage: BaseDataMessage | undefined,
): dataMessage is BaseDataMessage {
  return dataMessage?.type === DataMessageTypeEnum.Todos;
}

function isVisibleNonTodosPart(part: TerraUIMessagePart): boolean {
  if (!isDataOrConfirmationPart(part)) {
    if (part.type === 'text') {
      return hasTextContent(part);
    }

    return true;
  }

  if (!part.data) {
    return false;
  }

  const parsedDataMessage = parseBaseDataMessage(part.data);

  if (!parsedDataMessage) {
    return false;
  }

  if (
    parsedDataMessage.type === DataMessageTypeEnum.TraceMetadata ||
    parsedDataMessage.type === DataMessageTypeEnum.Todos
  ) {
    return false;
  }

  if (isToolCallData(parsedDataMessage)) {
    return false;
  }

  return true;
}

export type VisibleTodosResolution = {
  visibleTodosPartIds: Set<string>;
  backUpdatedTodosByPartId: Map<string, BaseDataMessage>;
};

export function resolveVisibleTodos(
  messages: TerraUIMessage[],
): VisibleTodosResolution {
  const visibleTodosPartIds = new Set<string>();
  const backUpdatedTodosByPartId = new Map<string, BaseDataMessage>();
  let latestVisibleTodosPartId: string | undefined;
  let visibleItemsSinceLatestTodos = Number.POSITIVE_INFINITY;

  messages.forEach((message) => {
    if (message.role === 'user') {
      if (message.parts.some(hasTextContent)) {
        visibleItemsSinceLatestTodos += 1;
      }

      return;
    }

    if (message.role !== 'assistant') {
      return;
    }

    filterSupersededToolCalls(message.parts).forEach((part) => {
      if (!isDataOrConfirmationPart(part) || !part.data) {
        if (isVisibleNonTodosPart(part)) {
          visibleItemsSinceLatestTodos += 1;
        }

        return;
      }

      const parsedDataMessage = parseBaseDataMessage(part.data);
      const partId = getPartId(part);

      if (isTodosDataMessage(parsedDataMessage) && partId) {
        if (
          visibleItemsSinceLatestTodos >= MIN_VISIBLE_ITEMS_BETWEEN_TODOS ||
          !latestVisibleTodosPartId
        ) {
          visibleTodosPartIds.add(partId);
          backUpdatedTodosByPartId.set(partId, parsedDataMessage);
          latestVisibleTodosPartId = partId;
          visibleItemsSinceLatestTodos = 0;

          return;
        }

        backUpdatedTodosByPartId.set(
          latestVisibleTodosPartId,
          parsedDataMessage,
        );

        return;
      }

      if (isVisibleNonTodosPart(part)) {
        visibleItemsSinceLatestTodos += 1;
      }
    });
  });

  return { visibleTodosPartIds, backUpdatedTodosByPartId };
}
